import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { RefreshCw, LogOut, Search, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { api, clearToken, getToken, formatApiError } from "../lib/api";
import { BookingCard, STATUS } from "../components/admin/BookingCard";

const FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [q, setQ] = useState("");

  const onErr = useCallback((err) => {
    if (err.response?.status === 401) {
      clearToken();
      nav("/admin/login");
    } else toast.error(formatApiError(err.response?.data?.detail));
  }, [nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.listBookings();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      onErr(err);
    } finally {
      setLoading(false);
    }
  }, [onErr]);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const patch = async (id, body, msg) => {
    try {
      const { data } = await api.updateBooking(id, body);
      setItems((p) => p.map((b) => (b.id === id ? data : b)));
      toast.success(msg);
    } catch (err) {
      onErr(err);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.deleteBooking(id);
      setItems((p) => p.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch (err) {
      onErr(err);
    }
  };

  const counts = useMemo(() => FILTERS.reduce((a, f) => ({ ...a, [f]: f === "all" ? items.length : items.filter((b) => b.status === f).length }), {}), [items]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((b) => (filter === "all" || b.status === filter) && (!s || [b.name, b.phone, b.address, b.vehicle_make, b.vehicle_model].join(" ").toLowerCase().includes(s)));
  }, [items, filter, q]);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#07070b]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-home-link"><img src="/img/logo-sm.webp" alt="Bridgeway Auto" className="h-11 w-auto" /></Link>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="rounded-full border border-white/15 p-2.5 hover:border-white/40" aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={() => { clearToken(); nav("/admin/login"); }} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-white/40"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <h1 className="font-race text-5xl sm:text-6xl">Appointments</h1>
        <p className="mt-2 text-zinc-300">Call each new request to confirm, then mark it confirmed.</p>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-2xl border p-4 text-left transition-colors ${filter === f ? "border-[var(--mag)] bg-[var(--mag)]/10" : "border-white/10 bg-[var(--surface)] hover:border-white/30"}`}>
              <div className="font-race text-4xl" data-testid={`admin-count-${f}`}>{counts[f] ?? 0}</div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">{f === "all" ? "All" : STATUS[f].label}</div>
            </button>
          ))}
        </div>
        <div className="relative mt-6">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, address, vehicle" className="h-12 rounded-xl border-white/10 bg-[var(--surface)] pl-11 text-white placeholder:text-zinc-500" data-testid="admin-search-input" />
        </div>
        <div className="mt-6 space-y-4">
          {shown.map((b) => (
            <BookingCard key={b.id} b={b} onStatus={(id, s) => patch(id, { status: s }, `Marked ${STATUS[s].label.toLowerCase()}`)} onNotes={(id, n) => patch(id, { admin_notes: n }, "Notes saved")} onDelete={remove} />
          ))}
          {!loading && !shown.length && (
            <div data-testid="admin-empty-state" className="flex flex-col items-center rounded-3xl border border-dashed border-white/15 py-20 text-zinc-400">
              <Inbox className="h-10 w-10" />
              <p className="mt-3">No {filter === "all" ? "" : STATUS[filter].label.toLowerCase()} bookings.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
